/**
 * Backend contract (Phase 3 + 4):
 * - GET    /counseling/session-notes?studentId=        -> lightweight list by student
 * - POST   /counseling/session-notes                      -> create note
 * - POST   /counseling/session-notes/{id}/audio           -> upload audio; server enqueues AI
 * - GET    /counseling/session-notes/{id}                 -> note without transcript
 * - GET    /counseling/session-notes/{id}/transcript      -> transcript only
 * - GET    /counseling/session-notes/{id}/ai-jobs/latest  -> poll AI status
 * - POST   /counseling/session-notes/{id}/ai-jobs/{jobId}/retry
 * - DELETE /counseling/session-notes/{id}
 * - GET    /counseling/session-notes/{id}/audio
 * - PATCH  /counseling/session-notes/{id}
 * - POST   /counseling/session-notes/{id}/action-items
 * - PATCH  /counseling/session-notes/{id}/action-items/{itemId}
 * - DELETE /counseling/session-notes/{id}/action-items/{itemId}
 *
 * Set REACT_APP_NOTE_TAKER_SESSION_API=false|stub to force the in-memory stub.
 * Default: real backend (same pattern as noteTakerSpeechApi).
 */

const STUB_FAIL_CREATE = process.env.REACT_APP_SESSION_STUB_FAIL_CREATE === 'true'
const STUB_FAIL_UPLOAD = process.env.REACT_APP_SESSION_STUB_FAIL_UPLOAD === 'true'

function isStubFailAiEnabled() {
	return process.env.REACT_APP_SESSION_STUB_FAIL_AI === 'true'
}

const STUB_AI_QUEUED_MS = Number(process.env.REACT_APP_SESSION_STUB_AI_QUEUED_MS || 600)
const STUB_AI_PROCESSING_MS = Number(process.env.REACT_APP_SESSION_STUB_AI_PROCESSING_MS || 1400)

const stubNotesByRecordingId = new Map()
const stubNotesById = new Map()
const stubAiTimersByNoteId = new Map()
let axiosInstancePromise = null

function shouldUseRealApi() {
	const flag = String(process.env.REACT_APP_NOTE_TAKER_SESSION_API || '')
		.trim()
		.toLowerCase()
	if (flag === 'false' || flag === 'stub') return false
	// Jest runs without env files — keep the stub unless a test opts into real API.
	if (process.env.NODE_ENV === 'test' && flag !== 'true') return false
	return true
}

async function getAxiosInstance() {
	if (!axiosInstancePromise) {
		axiosInstancePromise = import('./api').then(module => module.default)
	}
	return axiosInstancePromise
}

function delay(ms, signal) {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason || new DOMException('Aborted', 'AbortError'))
			return
		}

		const timeoutId = window.setTimeout(resolve, ms)
		const onAbort = () => {
			window.clearTimeout(timeoutId)
			reject(signal?.reason || new DOMException('Aborted', 'AbortError'))
		}

		signal?.addEventListener('abort', onAbort, { once: true })
	})
}

function createStubSessionNoteId() {
	return `sn-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function createStubAiJobId() {
	return `job-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function createStubActionItemId() {
	return `item-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

function touchStubNoteUpdatedAt(note) {
	note.updatedAt = new Date().toISOString()
	return note.updatedAt
}

function clearStubAiTimers(sessionNoteId) {
	const timers = stubAiTimersByNoteId.get(sessionNoteId) || []
	timers.forEach(timerId => window.clearTimeout(timerId))
	stubAiTimersByNoteId.delete(sessionNoteId)
}

function getLatestStubJob(note) {
	const jobs = note?.aiJobs || []
	return jobs.length > 0 ? jobs[jobs.length - 1] : null
}

function buildStubAiArtifacts(payload = {}) {
	const transcriptText = String(payload.transcriptText || '').trim()
	const firstSentence = transcriptText.split(/[.!?]/).find(Boolean)?.trim()
	const topic = firstSentence || 'Session review'

	return {
		sessionTitle: `Session: ${topic.slice(0, 80)}`,
		sessionSummary:
			transcriptText.length > 0
				? `${transcriptText.slice(0, 280)}${transcriptText.length > 280 ? '...' : ''}`
				: 'Summary will appear here once transcription is available.',
		actionItems: [
			{
				text: 'Follow up on the main topic discussed in this session.',
				ownerType: 'counselor',
				ownerLabel: 'You',
				dueHint: null,
			},
			{
				text: 'Review next steps and share any outstanding materials.',
				ownerType: 'student',
				ownerLabel: null,
				dueHint: null,
			},
		],
	}
}

function mapStubActionItemsForApi(actionItems = []) {
	return actionItems.map((item, index) => ({
		id: `ai-action-${index + 1}-${Math.random().toString(36).slice(2, 7)}`,
		text: String(item.text || ''),
		ownerType: item.ownerType || 'counselor',
		ownerLabel: item.ownerLabel ?? null,
		sortOrder: index,
		status: 'open',
		source: 'ai_generated',
		dueHint: item.dueHint ?? null,
	}))
}

function applyStubAiSuccess(note, job, output) {
	job.status = 'completed'
	job.error = null
	job.completedAt = new Date().toISOString()
	job.outputJson = output

	note.status = 'completed'
	note.title = output.sessionTitle
	note.summary = output.sessionSummary
	note.actionItems = mapStubActionItemsForApi(output.actionItems)
	note.aiCompletedAt = job.completedAt
	note.latestJobId = job.id
	touchStubNoteUpdatedAt(note)
}

function scheduleStubAiProgress(note) {
	clearStubAiTimers(note.id)

	const job = getLatestStubJob(note)
	if (!job || job.status !== 'queued') return

	const timers = []

	timers.push(
		window.setTimeout(() => {
			const currentNote = stubNotesById.get(note.id)
			const currentJob = getLatestStubJob(currentNote)
			if (!currentNote || !currentJob || currentJob.id !== job.id) return
			if (currentJob.status !== 'queued') return

			currentNote.status = 'ai_processing'
			currentJob.status = 'processing'
			currentJob.startedAt = new Date().toISOString()
		}, STUB_AI_QUEUED_MS),
	)

	timers.push(
		window.setTimeout(() => {
			const currentNote = stubNotesById.get(note.id)
			const currentJob = getLatestStubJob(currentNote)
			if (!currentNote || !currentJob || currentJob.id !== job.id) return
			if (currentJob.status === 'completed' || currentJob.status === 'failed') return

			if (isStubFailAiEnabled()) {
				currentJob.status = 'failed'
				currentJob.error = {
					code: 'AiProcessingError',
					message: 'Stub AI processing failed',
				}
				currentNote.status = 'failed'
				return
			}

			const output = buildStubAiArtifacts(currentNote.payload)
			applyStubAiSuccess(currentNote, currentJob, output)
		}, STUB_AI_QUEUED_MS + STUB_AI_PROCESSING_MS),
	)

	stubAiTimersByNoteId.set(note.id, timers)
}

function enqueueStubInitialAiJob(note) {
	clearStubAiTimers(note.id)

	const job = {
		id: createStubAiJobId(),
		status: 'queued',
		operation: 'initial_generation',
		parentJobId: null,
		rootJobId: null,
		error: null,
		createdAt: new Date().toISOString(),
		startedAt: null,
		completedAt: null,
		outputJson: null,
	}

	job.rootJobId = job.id
	note.aiJobs = [...(note.aiJobs || []), job]
	note.status = 'ai_processing'
	note.latestJobId = job.id

	scheduleStubAiProgress(note)
	return job
}

function buildSessionNoteResponse(note) {
	const latestJob = getLatestStubJob(note)

	return {
		id: note.id,
		status: note.status,
		studentId: note.studentId,
		recordingId: note.recordingId,
		createdAt: note.createdAt,
		updatedAt: note.updatedAt ?? note.createdAt,
		durationMs: note.payload?.durationMs ?? null,
		language: note.payload?.language ?? 'en-US',
		audioSizeBytes: note.audioSizeBytes ?? null,
		title: note.title ?? null,
		summary: note.summary ?? null,
		actionItems: note.actionItems ?? null,
		ai: latestJob
			? {
					latestJobId: latestJob.id,
					status: latestJob.status,
					operation: latestJob.operation,
					error: latestJob.error,
					completedAt: latestJob.completedAt,
			  }
			: null,
	}
}

async function createSessionNoteStub(payload, { signal } = {}) {
	const latencyMs = 350 + Math.floor(Math.random() * 450)
	await delay(latencyMs, signal)

	if (STUB_FAIL_CREATE) {
		const error = new Error('Stub session note creation failed')
		error.name = 'SessionCreationError'
		throw error
	}

	const existing = stubNotesByRecordingId.get(payload.recordingId)
	if (existing) {
		return { ...existing }
	}

	const id = createStubSessionNoteId()
	const note = {
		id,
		status: 'creating',
		studentId: payload.studentId,
		recordingId: payload.recordingId,
		createdAt: new Date().toISOString(),
		updatedAt: new Date().toISOString(),
		audioUploadUrl: `/counseling/session-notes/${id}/audio`,
		payload,
		aiJobs: [],
		title: null,
		summary: null,
		actionItems: null,
		latestJobId: null,
	}

	stubNotesByRecordingId.set(payload.recordingId, {
		id: note.id,
		status: note.status,
		studentId: note.studentId,
		recordingId: note.recordingId,
		createdAt: note.createdAt,
		audioUploadUrl: note.audioUploadUrl,
	})
	stubNotesById.set(id, note)

	return {
		id,
		status: 'creating',
		studentId: payload.studentId,
		recordingId: payload.recordingId,
		createdAt: note.createdAt,
		audioUploadUrl: note.audioUploadUrl,
	}
}

async function uploadSessionAudioStub(sessionNoteId, blob, mimeType, { signal } = {}) {
	void mimeType

	const latencyMs = 500 + Math.floor(Math.random() * 700)
	await delay(latencyMs, signal)

	if (STUB_FAIL_UPLOAD) {
		const error = new Error('Stub audio upload failed')
		error.name = 'AudioUploadError'
		throw error
	}

	const note = stubNotesById.get(sessionNoteId)
	if (!note) {
		const error = new Error(`Session note ${sessionNoteId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	note.status = 'created'
	note.audioSizeBytes = blob?.size || 0
	enqueueStubInitialAiJob(note)

	stubNotesByRecordingId.set(note.recordingId, {
		id: note.id,
		status: note.status,
		studentId: note.studentId,
		recordingId: note.recordingId,
		createdAt: note.createdAt,
		audioUploadUrl: note.audioUploadUrl,
	})

	return {
		id: sessionNoteId,
		status: 'created',
		audioSizeBytes: note.audioSizeBytes,
	}
}

function buildStubSessionNoteListItem(note) {
	return {
		id: note.id,
		status: note.status,
		studentId: note.studentId,
		title: note.title ?? null,
		summary: note.summary ?? null,
		createdAt: note.createdAt,
		updatedAt: note.updatedAt ?? note.createdAt,
		durationMs: note.payload?.durationMs ?? null,
		actionItemCount: Array.isArray(note.actionItems) ? note.actionItems.length : 0,
	}
}

async function listSessionNotesForStudentStub(studentId, { signal } = {}) {
	await delay(120, signal)

	const normalizedStudentId = String(studentId || '').trim()
	const notes = Array.from(stubNotesById.values())
		.filter(note => String(note.studentId || '').trim() === normalizedStudentId)
		.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)))
		.map(buildStubSessionNoteListItem)

	return { notes, total: notes.length }
}

async function deleteSessionNoteStub(sessionNoteId, { signal } = {}) {
	await delay(150, signal)

	clearStubAiTimers(sessionNoteId)

	const note = stubNotesById.get(sessionNoteId)
	if (!note) return { id: sessionNoteId, deleted: true }

	stubNotesById.delete(sessionNoteId)
	stubNotesByRecordingId.delete(note.recordingId)
	return { id: sessionNoteId, deleted: true }
}

async function getSessionNoteStub(sessionNoteId) {
	const note = stubNotesById.get(sessionNoteId)
	if (!note) {
		const error = new Error(`Session note ${sessionNoteId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	return buildSessionNoteResponse(note)
}

async function getSessionTranscriptStub(sessionNoteId) {
	const note = stubNotesById.get(sessionNoteId)
	if (!note) {
		const error = new Error(`Session note ${sessionNoteId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	return {
		transcriptText: String(note.payload?.transcriptText || ''),
		transcriptSegments: note.payload?.transcriptSegments || [],
	}
}

async function getLatestAiJobStub(sessionNoteId) {
	const note = stubNotesById.get(sessionNoteId)
	if (!note) {
		const error = new Error(`Session note ${sessionNoteId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	const job = getLatestStubJob(note)
	if (!job) {
		const error = new Error(`No AI job for session note ${sessionNoteId}`)
		error.name = 'NotFoundError'
		throw error
	}

	return {
		jobId: job.id,
		status: job.status,
		operation: job.operation,
		error: job.error,
		completedAt: job.completedAt,
	}
}

async function retryAiJobStub(sessionNoteId, jobId, { signal } = {}) {
	await delay(200, signal)

	const note = stubNotesById.get(sessionNoteId)
	if (!note) {
		const error = new Error(`Session note ${sessionNoteId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	const failedJob = (note.aiJobs || []).find(job => job.id === jobId)
	if (!failedJob) {
		const error = new Error(`AI job ${jobId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	if (failedJob.status === 'queued' || failedJob.status === 'processing') {
		return {
			jobId: failedJob.id,
			status: failedJob.status,
			operation: failedJob.operation,
		}
	}

	if (failedJob.status === 'completed') {
		const error = new Error('AI job already completed')
		error.name = 'ConflictError'
		throw error
	}

	const retryJob = {
		id: createStubAiJobId(),
		status: 'queued',
		operation: failedJob.operation,
		parentJobId: failedJob.id,
		rootJobId: failedJob.rootJobId || failedJob.id,
		error: null,
		createdAt: new Date().toISOString(),
		startedAt: null,
		completedAt: null,
		outputJson: null,
	}

	note.aiJobs.push(retryJob)
	note.status = 'ai_processing'
	note.latestJobId = retryJob.id
	note.title = null
	note.summary = null
	note.actionItems = null

	scheduleStubAiProgress(note)

	return {
		jobId: retryJob.id,
		status: retryJob.status,
		operation: retryJob.operation,
	}
}

export async function createSessionNote(payload, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.post('/counseling/session-notes', payload, {
			signal: options.signal,
		})
		return data
	}

	return createSessionNoteStub(payload, options)
}

export async function uploadSessionAudio(sessionNoteId, blob, mimeType, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const formData = new FormData()
		const resolvedMime = mimeType || blob?.type || 'audio/webm'
		const extension = resolvedMime.includes('mp4') ? 'mp4' : 'webm'
		formData.append(
			'file',
			blob,
			`recording.${extension}`,
		)
		formData.append('mimeType', resolvedMime)

		const { data } = await axiosInstance.post(
			`/counseling/session-notes/${encodeURIComponent(sessionNoteId)}/audio`,
			formData,
			{
				// Let the browser set multipart boundary; default JSON Content-Type
				// on the axios instance would otherwise break the upload.
				headers: { 'Content-Type': undefined },
				signal: options.signal,
				onUploadProgress: options.onUploadProgress,
			},
		)
		return data
	}

	return uploadSessionAudioStub(sessionNoteId, blob, mimeType, options)
}

export async function getSessionNote(sessionNoteId, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.get(
			`/counseling/session-notes/${encodeURIComponent(sessionNoteId)}`,
			{ signal: options.signal },
		)
		return data
	}

	return getSessionNoteStub(sessionNoteId)
}

export async function getSessionTranscript(sessionNoteId, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.get(
			`/counseling/session-notes/${encodeURIComponent(sessionNoteId)}/transcript`,
			{ signal: options.signal },
		)
		return data
	}

	return getSessionTranscriptStub(sessionNoteId)
}

export async function getLatestAiJob(sessionNoteId, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.get(
			`/counseling/session-notes/${encodeURIComponent(sessionNoteId)}/ai-jobs/latest`,
			{ signal: options.signal },
		)
		return data
	}

	return getLatestAiJobStub(sessionNoteId)
}

export async function retryAiJob(sessionNoteId, jobId, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.post(
			`/counseling/session-notes/${encodeURIComponent(sessionNoteId)}/ai-jobs/${encodeURIComponent(jobId)}/retry`,
			{},
			{ signal: options.signal },
		)
		return data
	}

	return retryAiJobStub(sessionNoteId, jobId, options)
}

async function getSessionAudioUrlStub(sessionNoteId) {
	const note = stubNotesById.get(sessionNoteId)
	if (!note) {
		const error = new Error(`Session note ${sessionNoteId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	return {
		url: `https://stub.kandor.local/session-notes/${sessionNoteId}/audio.webm`,
		expiresIn: 3600,
		mimeType: 'audio/webm',
		durationMs: note.payload?.durationMs ?? null,
	}
}

async function updateSessionSummaryStub(sessionNoteId, { summary, updatedAt }, { signal } = {}) {
	await delay(120, signal)

	const note = stubNotesById.get(sessionNoteId)
	if (!note) {
		const error = new Error(`Session note ${sessionNoteId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	const serverUpdatedAt = note.updatedAt ?? note.createdAt
	if (updatedAt && serverUpdatedAt && updatedAt !== serverUpdatedAt) {
		const error = new Error('Session note was updated elsewhere')
		error.name = 'ConflictError'
		throw error
	}

	note.summary = String(summary ?? '')
	const nextUpdatedAt = touchStubNoteUpdatedAt(note)

	return {
		id: note.id,
		summary: note.summary,
		updatedAt: nextUpdatedAt,
	}
}

async function createSessionActionItemStub(sessionNoteId, payload, { signal } = {}) {
	await delay(120, signal)

	const text = String(payload?.text || '').trim()
	if (!text) {
		const error = new Error('Action item text is required')
		error.name = 'ValidationError'
		throw error
	}

	const note = stubNotesById.get(sessionNoteId)
	if (!note) {
		const error = new Error(`Session note ${sessionNoteId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	const items = Array.isArray(note.actionItems) ? [...note.actionItems] : []
	const item = {
		id: createStubActionItemId(),
		text,
		ownerType: payload?.ownerType || 'counselor',
		ownerLabel: payload?.ownerLabel ?? null,
		status: 'open',
		sortOrder:
			Number.isFinite(Number(payload?.sortOrder)) ? Number(payload.sortOrder) : items.length,
		source: 'manual',
		dueHint: payload?.dueHint ?? null,
	}

	items.push(item)
	note.actionItems = items
	touchStubNoteUpdatedAt(note)

	return { ...item }
}

async function updateSessionActionItemStub(sessionNoteId, itemId, payload, { signal } = {}) {
	await delay(120, signal)

	const note = stubNotesById.get(sessionNoteId)
	if (!note) {
		const error = new Error(`Session note ${sessionNoteId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	const items = Array.isArray(note.actionItems) ? note.actionItems : []
	const index = items.findIndex(item => item.id === itemId)
	if (index < 0) {
		const error = new Error(`Action item ${itemId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	const current = items[index]
	const next = {
		...current,
		...(payload?.text != null ? { text: String(payload.text) } : {}),
		...(payload?.ownerType != null ? { ownerType: payload.ownerType } : {}),
		...(payload?.ownerLabel !== undefined ? { ownerLabel: payload.ownerLabel } : {}),
		...(payload?.status != null ? { status: payload.status } : {}),
		...(payload?.sortOrder != null ? { sortOrder: Number(payload.sortOrder) } : {}),
	}

	items[index] = next
	note.actionItems = items
	touchStubNoteUpdatedAt(note)

	return { ...next }
}

async function deleteSessionActionItemStub(sessionNoteId, itemId, { signal } = {}) {
	await delay(120, signal)

	const note = stubNotesById.get(sessionNoteId)
	if (!note) {
		const error = new Error(`Session note ${sessionNoteId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	const items = Array.isArray(note.actionItems) ? note.actionItems : []
	const index = items.findIndex(item => item.id === itemId)
	if (index < 0) {
		const error = new Error(`Action item ${itemId} not found`)
		error.name = 'NotFoundError'
		throw error
	}

	note.actionItems = items.filter(item => item.id !== itemId)
	touchStubNoteUpdatedAt(note)

	return { id: itemId, deleted: true }
}

export async function getSessionAudioUrl(sessionNoteId, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.get(
			`/counseling/session-notes/${encodeURIComponent(sessionNoteId)}/audio`,
			{ signal: options.signal },
		)
		return data
	}

	return getSessionAudioUrlStub(sessionNoteId)
}

export async function updateSessionSummary(sessionNoteId, payload, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.patch(
			`/counseling/session-notes/${encodeURIComponent(sessionNoteId)}`,
			payload,
			{ signal: options.signal },
		)
		return data
	}

	return updateSessionSummaryStub(sessionNoteId, payload, options)
}

export async function createSessionActionItem(sessionNoteId, payload, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.post(
			`/counseling/session-notes/${encodeURIComponent(sessionNoteId)}/action-items`,
			payload,
			{ signal: options.signal },
		)
		return data
	}

	return createSessionActionItemStub(sessionNoteId, payload, options)
}

export async function updateSessionActionItem(sessionNoteId, itemId, payload, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.patch(
			`/counseling/session-notes/${encodeURIComponent(sessionNoteId)}/action-items/${encodeURIComponent(itemId)}`,
			payload,
			{ signal: options.signal },
		)
		return data
	}

	return updateSessionActionItemStub(sessionNoteId, itemId, payload, options)
}

export async function deleteSessionActionItem(sessionNoteId, itemId, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.delete(
			`/counseling/session-notes/${encodeURIComponent(sessionNoteId)}/action-items/${encodeURIComponent(itemId)}`,
			{ signal: options.signal },
		)
		return data
	}

	return deleteSessionActionItemStub(sessionNoteId, itemId, options)
}

export async function listSessionNotesForStudent(studentId, options = {}) {
	const normalizedStudentId = String(studentId || '').trim()
	if (!normalizedStudentId) {
		const error = new Error('studentId is required')
		error.name = 'ValidationError'
		throw error
	}

	if (shouldUseRealApi()) {
		const params = new URLSearchParams({ studentId: normalizedStudentId })
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.get(
			`/counseling/session-notes?${params.toString()}`,
			{ signal: options.signal },
		)
		return data
	}

	return listSessionNotesForStudentStub(normalizedStudentId, options)
}

export async function deleteSessionNote(sessionNoteId, options = {}) {
	if (shouldUseRealApi()) {
		const axiosInstance = await getAxiosInstance()
		const { data } = await axiosInstance.delete(
			`/counseling/session-notes/${encodeURIComponent(sessionNoteId)}`,
			{ signal: options.signal },
		)
		return data
	}

	return deleteSessionNoteStub(sessionNoteId, options)
}

/** Test-only reset for stub state. */
export function __resetSessionApiStubForTests() {
	stubNotesByRecordingId.clear()
	stubNotesById.forEach(note => clearStubAiTimers(note.id))
	stubNotesById.clear()
	stubAiTimersByNoteId.clear()
}

/** Test-only: advance stub AI timers synchronously in tests. */
export function __runStubAiTimersForTests() {
	stubAiTimersByNoteId.forEach(timers => {
		timers.forEach(timerId => {
			window.clearTimeout(timerId)
		})
	})
	stubAiTimersByNoteId.clear()

	stubNotesById.forEach(note => {
		const job = getLatestStubJob(note)
		if (!job || job.status === 'completed' || job.status === 'failed') return

		if (job.status === 'queued') {
			note.status = 'ai_processing'
			job.status = 'processing'
			job.startedAt = new Date().toISOString()
		}

		if (isStubFailAiEnabled()) {
			job.status = 'failed'
			job.error = { code: 'AiProcessingError', message: 'Stub AI processing failed' }
			note.status = 'failed'
			return
		}

		const output = buildStubAiArtifacts(note.payload)
		applyStubAiSuccess(note, job, output)
	})
}
